import { useState, useCallback, useRef, useEffect } from 'react';

const STORAGE_KEY = 'post-creator-split-ratio';
const DEFAULT_RATIO = 55; // left column percentage
const MIN_RATIO = 35;
const MAX_RATIO = 65;
/** Minimum pixel width for the left column (toolbar must remain visible) */
const MIN_LEFT_PX = 360;

/**
 * Hook for resizable two-column layout with localStorage persistence.
 * Returns leftPercent for use as inline styles — no direct DOM manipulation.
 */
export function useResizableColumns(active: boolean) {
  const [leftPercent, setLeftPercent] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const val = parseFloat(stored);
        if (val >= MIN_RATIO && val <= MAX_RATIO) return val;
      }
    } catch { /* ignore */ }
    return DEFAULT_RATIO;
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    isDragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    // Enforce both percentage and pixel minimums so the toolbar never gets clipped
    const minPxRatio = (MIN_LEFT_PX / rect.width) * 100;
    const effectiveMin = Math.max(MIN_RATIO, minPxRatio);
    const percent = Math.min(MAX_RATIO, Math.max(effectiveMin, (x / rect.width) * 100));
    setLeftPercent(percent);
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Math.round(leftPercent)));
    } catch { /* ignore */ }
  }, [leftPercent]);

  // active is kept in signature for API compatibility
  void active;

  return {
    containerRef,
    leftPercent,
    handleProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
    },
  };
}
